import React, {useEffect} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/common/SafeIcon';
import SEO from '@/components/SEO';
import {BARS} from '@/data/bars';
import {useLanguage} from '../context/LanguageContext';
import LanguageToggle from '@/components/LanguageToggle';

const {FiArrowLeft} = FiIcons;

export default function Menu() {
  const {id} = useParams();
  const navigate = useNavigate();
  const {t, lang} = useLanguage();
  const bar = BARS.find((item) => item.id === id);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [bar]);

  if (!bar) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-50 flex items-center justify-center">
        <p>Menu not found.</p>
      </div>
    );
  }

  const translatedCategory = lang === 'es' ? bar.category_es : bar.category_en;
  const translatedDescription = lang === 'es'
    ? bar.description_es
    : bar.description_en;
  const seoTitle = `${bar.name} | Menú & ${translatedCategory} | The Selection Barberà`;
  const metaDescription = `${translatedDescription.substring(0, 150)}...`;

  const menuSchema = {
    '@context': 'https://schema.org',
    '@type': bar.id === 'craftbeer' ? 'Brewery' : 'Restaurant',
    name: bar.name,
    image: bar.image,
    description: translatedDescription,
    url: `https://barberadelvalles.bar/menu/${bar.id}`,
    telephone: bar.phone,
    address: {
      '@type': 'PostalAddress',
      streetAddress: bar.address.split(',')[0],
      addressLocality: 'Barberà del Vallès',
      postalCode: '08210',
      addressCountry: 'ES'
    },
    hasMenu: {
      '@type': 'Menu',
      name: `${bar.name} Selection`,
      hasMenuItem: bar.menu.map((item) => ({
        '@type': 'MenuItem',
        name: item.name,
        description: lang === 'es' ? item.desc_es : item.desc_en,
        offers: {
          '@type': 'Offer',
          price: item.price.replace('€', '').trim(),
          priceCurrency: 'EUR'
        }
      }))
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50 pb-24">
      <SEO title={seoTitle} description={metaDescription} schema={menuSchema} />
      <LanguageToggle />

      <header className="sticky top-0 z-50 bg-zinc-950/80 backdrop-blur-md border-b border-zinc-900 px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center">
          <button
            onClick={() => navigate('/')}
            className="p-2 -ml-2 text-zinc-400 hover:text-zinc-50 transition-colors flex items-center gap-2 group"
            aria-label="Back to home"
          >
            <SafeIcon icon={FiArrowLeft} className="text-xl group-hover:-translate-x-1 transition-transform" />
            <span className="text-xs font-bold tracking-widest uppercase">{t.ui.back}</span>
          </button>
          <div className="h-4 w-px bg-zinc-800 mx-4" aria-hidden="true" />
          <nav className="text-[10px] font-bold tracking-widest text-zinc-600 uppercase">
            {t.ui.directory} / {bar.name}
          </nav>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 mt-12" role="main">
        <motion.div
          key={bar.id}
          initial={{opacity: 0, y: 20}}
          animate={{opacity: 1, y: 0}}
          transition={{duration: 0.6}}
        >
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2">
            {bar.name}
          </h1>
          <p className="text-zinc-400 text-lg font-light mb-8">{translatedCategory}</p>

          <address className="not-italic text-sm text-zinc-500 mb-12 border-l border-zinc-800 pl-4">
            <p>{bar.address}</p>
            <p className="mt-1">{bar.phone}</p>
          </address>

          <div className="space-y-10">
            {bar.menu.map((item, index) => (
              <motion.div
                key={index}
                initial={{opacity: 0, y: 10}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.4, delay: 0.05 * index}}
                className="group"
              >
                <div className="flex justify-between items-baseline mb-1">
                  <h2 className="text-lg font-medium text-zinc-100 group-hover:text-white transition-colors">
                    {item.name}
                  </h2>
                  <div
                    className="flex-1 border-b border-dotted border-zinc-800 mx-4 opacity-50 relative top-[-6px]"
                    aria-hidden="true"
                  />
                  <span className="text-lg font-medium text-zinc-300">{item.price}</span>
                </div>
                <p className="text-sm text-zinc-500 font-light pr-16 leading-relaxed">
                  {lang === 'es' ? item.desc_es : item.desc_en}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </main>
    </div>
  );
}